import React, { useState, useEffect } from 'react';
import { Box, Typography, Dialog, IconButton, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from 'i18next';
import type { WalkdownImageData } from '../../types/walkdown';

interface WalkdownImagesSectionProps {
  open: boolean;
  onClose: () => void;
  deviceTag: string;
  walkdownNumber: string; // <-- change from walkdownId to walkdownNumber
  date: string;
  images: WalkdownImageData[];
}

const WalkdownImagesSection: React.FC<WalkdownImagesSectionProps> = ({
  open,
  onClose,
  deviceTag,
  walkdownNumber, // <-- change from walkdownId to walkdownNumber
  date,  
  images,
}) => {
  const [selectedImage, setSelectedImage] = useState<WalkdownImageData | null>(null);

  useEffect(() => {
    // Always reset to the first image when the image set changes (e.g. switching devices)
    if (images.length > 0) {
      const primaryImage = images.find(img => img.primary_image) || images[0];
      setSelectedImage(primaryImage);
    } else {
      setSelectedImage(null);
    }
  }, [images]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="walkdown-images-modal">
        <DialogTitle className="walkdown-images-header" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t('walkdownImages', 'Walkdown Images')}
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: "#666", padding: "4px" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: 3 }}>
          {/* Title */}
          <Typography className="walkdown-images-header">
            {t("walkdownImagesFor", "Walkdown Images for")}{" "}
            <span style={{ fontWeight: 400 }}>[{deviceTag}]</span>
          </Typography>
          <Typography className="walkdown-images-walkdown-number">
            {t("walkdown", "Walkdown")} {walkdownNumber}
          </Typography>
          <Typography className="walkdown-images-date">
            {date} ({images.length} {t("image", "Image")}{images.length !== 1 ? ` ${t("images", "Images")}` : ""})
          </Typography>

          {images.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t(
                  "noImagesAvailableForDevice",
                  "No images available for this device",
                )}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Thumbnails */}
              <Box className="walkdown-images-thumbs">
                {images.map((img, i) => (
                  <Box
                    key={i}
                    className={`walkdown-images-thumb${selectedImage?.image_url === img.image_url ? ' selected' : ''}`}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img 
                      src={img.image_url} 
                      alt={img.image_title || `Thumbnail ${i + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  </Box>
                ))}
              </Box>

              {/* Main Preview */}
              <Box className="walkdown-images-main">
                {selectedImage && (
                  <img
                    src={selectedImage.image_url}
                    alt={selectedImage.image_title || t("Selected", "Selected")}
                  />
                )}
              </Box>
            </Box>
          )}
          {selectedImage && selectedImage.image_title && selectedImage.image_title.trim() && (
            <Typography className="walkdown-images-title">
              {selectedImage.image_title}
            </Typography>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default WalkdownImagesSection;
